/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */
import {
  AuthConnector,
  OAuth2,
  OAuth2CreateOptions,
  OAuth2Session,
} from '@backstage/core-app-api';
import {
  ApiRef,
  BackstageIdentityApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
  configApiRef,
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
} from '@backstage/core-plugin-api';
import type { SignInProviderConfig } from '@backstage/core-components';

export const m42EnterpriseAuthApiRef: ApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({ id: 'auth.m42-enterprise' });

export const REFRESH_LOCK_NAME = 'm42-enterprise-refresh';
export const REFRESH_LOCK_TIMEOUT_MS = 30_000;

// Same-origin tabs refresh one at a time. The backend vault already serialises
// refreshes of one session and keeps the rotated issuer token server-side, so this
// only keeps tabs from piling concurrent refresh calls onto it.
export function withAuthLock<T>(
  fn: () => Promise<T>,
  locks: LockManager | undefined = globalThis.navigator?.locks,
): Promise<T> {
  if (!locks) {
    return fn();
  }
  return locks.request(REFRESH_LOCK_NAME, async () => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(
        () =>
          reject(
            new Error(
              `m42-enterprise auth call timed out after ${REFRESH_LOCK_TIMEOUT_MS} ms`,
            ),
          ),
        REFRESH_LOCK_TIMEOUT_MS,
      );
    });
    // Inside the try, so a synchronous throw from fn still clears the timer.
    try {
      const call = fn();
      // The abandoned call may still reject after the timeout wins the race.
      call.catch(() => undefined);
      return await Promise.race([call, timeout]);
    } finally {
      clearTimeout(timer);
    }
  });
}

// createSession stays unlocked: the popup must open inside the user's click.
export function lockingAuthConnector<S>(
  inner: AuthConnector<S>,
  locks?: LockManager,
): AuthConnector<S> {
  return {
    createSession: options => inner.createSession(options),
    refreshSession: options =>
      withAuthLock(() => inner.refreshSession(options), locks),
    removeSession: () => withAuthLock(() => inner.removeSession(), locks),
  };
}

type DefaultConnectorFactory = (options: OAuth2CreateOptions) => AuthConnector<OAuth2Session>;

export const m42EnterpriseAuthApiFactory = createApiFactory({
  api: m42EnterpriseAuthApiRef,
  deps: {
    discoveryApi: discoveryApiRef,
    oauthRequestApi: oauthRequestApiRef,
    configApi: configApiRef,
  },
  factory: ({ discoveryApi, oauthRequestApi, configApi }) => {
    // DefaultAuthConnector is not exported; the private static builds it (pinned by a guard test).
    const createConnector = (OAuth2 as unknown as {
      createAuthConnector: DefaultConnectorFactory;
    }).createAuthConnector;
    const inner = createConnector.call(OAuth2, {
      discoveryApi,
      oauthRequestApi,
      configApi,
      provider: { id: 'm42-enterprise', title: 'Microsoft', icon: () => null },
      environment: configApi.getOptionalString('auth.environment'),
    });
    return OAuth2.create({
      authConnector: lockingAuthConnector(inner),
      defaultScopes: ['openid', 'profile', 'offline_access'],
    });
  },
});

export const m42EnterpriseProviderConfig: SignInProviderConfig = {
  id: 'm42-enterprise-auth-provider',
  title: 'Microsoft',
  message: 'Sign in with your Mission 42 Microsoft account',
  apiRef: m42EnterpriseAuthApiRef,
};
